import { useState, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { useBruktbilFilters } from '../useBruktbilFilters'
import FilterSlot, { useLocalFilter } from '../FilterSlot'
import { fetchMerkeOptions, fetchModellOptions, fetchObservasjoner } from './api'
import type { MerkeOption, ModellOption, ObservasjonerResponse } from './api'
import ObservasjonerGrid from './ObservasjonerGrid'

const PAGE_SIZE = 50

export default function PriserPage() {
  const { t } = useTranslation('bruktbil')
  const { from, to, klasse, varebil, omregistrering } = useBruktbilFilters()

  // #526 — these four used to auto-apply (merke/modell immediately, årsmodell
  // on a 400 ms debounce), so the observasjoner query refired while the filter
  // set was still half-configured. They are drafts now; the layout's single
  // Oppdater commits them. `*Input` is what the control shows, the second
  // value is what the query uses.
  const [merkeInput, setMerkeInput, appliedMerke] = useLocalFilter<string>('priserMerke', '')
  const [modellInput, setModellInput, appliedModell] = useLocalFilter<string>('priserModell', '')
  const [aarFrom, setAarFrom, appliedAarFrom] = useLocalFilter<string>('priserAarFrom', '')
  const [aarTo, setAarTo, appliedAarTo] = useLocalFilter<string>('priserAarTo', '')

  const [merkeOptions, setMerkeOptions] = useState<MerkeOption[]>([])
  const [modellOptions, setModellOptions] = useState<ModellOption[]>([])
  const [modellLoading, setModellLoading] = useState(false)

  const applied = {
    merke: appliedMerke ? [appliedMerke] : [],
    modell: appliedModell ? [appliedModell] : [],
    aarsmodell_from: appliedAarFrom ? parseInt(appliedAarFrom, 10) : undefined,
    aarsmodell_to: appliedAarTo ? parseInt(appliedAarTo, 10) : undefined,
  }
  const appliedKey = JSON.stringify(applied)

  const [page, setPage] = useState(1)
  const [sort, setSort] = useState('dato_overforing')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const [data, setData] = useState<ObservasjonerResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load Merke options (re-fetch whenever the global filters change)
  useLatest(alive => {
    fetchMerkeOptions({ from, to, klasse, varebil, omregistrering })
      .then(r => { if (alive()) setMerkeOptions(r.data.rows.filter(o => o.merke)) })
      .catch(() => { if (alive()) setMerkeOptions([]) })
  }, [from, to, JSON.stringify(klasse), varebil, omregistrering])

  // Load Modell options whenever Merke selection changes.
  // Clear old options immediately so the dropdown can't briefly show the
  // previous merke's modeller before the new fetch returns.
  useLatest(alive => {
    setModellOptions([])
    if (!merkeInput) return
    setModellLoading(true)
    fetchModellOptions({ from, to, klasse, varebil, omregistrering }, [merkeInput])
      .then(r => {
        if (!alive()) return
        const sorted = r.data.rows
          .filter(o => o.modell)
          .sort((a, b) => (a.modell ?? '').localeCompare(b.modell ?? '', 'nb-NO'))
        setModellOptions(sorted)
      })
      .catch(() => { if (alive()) setModellOptions([]) })
      .finally(() => { if (alive()) setModellLoading(false) })
  }, [merkeInput, from, to, JSON.stringify(klasse), varebil, omregistrering])

  // `appliedKey`, not `applied`: the latter is rebuilt on every render, so
  // Object.is always saw a new identity and this effect refired on each
  // setData/setLoading — a self-sustaining fetch loop. useLatest supplies the
  // other half: without it a slow response is not replaced by the next one, it
  // is *stacked* on top of it, and each in-flight request holds a DB connection
  // for its whole duration.
  useLatest(alive => {
    setLoading(true)
    setError(null)
    fetchObservasjoner({
      from, to, klasse, varebil, omregistrering,
      merke: applied.merke,
      modell: applied.modell,
      aarsmodell_from: applied.aarsmodell_from,
      aarsmodell_to: applied.aarsmodell_to,
      page,
      page_size: PAGE_SIZE,
      sort,
      sort_dir: sortDir,
    })
      .then(r => { if (alive()) setData(r.data) })
      // '' = no message to show; the fallback is translated at render.
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : '') })
      .finally(() => { if (alive()) setLoading(false) })
  }, [from, to, JSON.stringify(klasse), varebil, omregistrering, appliedKey, page, sort, sortDir])

  // Back to page 1 whenever a newly committed filter set arrives.
  useEffect(() => { setPage(1) }, [appliedKey])

  function handleSort(key: string) {
    if (sort === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSort(key)
      setSortDir('desc')
    }
    setPage(1)
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <div className="space-y-4">
      {/* Explanation box */}
      <div className="card p-4 text-sm text-gray-700 dark:text-gray-300 border-l-4 border-primary-500">
        <h2 className="font-semibold text-base mb-2">{t('priser.om.title')}</h2>
        <p className="mb-2">
          <Trans t={t} i18nKey="priser.om.p1" components={{ b: <strong /> }} />
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          <Trans t={t} i18nKey="priser.om.p2" components={{ b: <strong />, em: <em /> }} />
        </p>
      </div>

      {/* Filters — portalled into the layout's filter box so the single
          Oppdater/Nullstill pair there commits them too (#526). */}
      <FilterSlot>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <label className="text-sm">
            <div className="text-gray-600 dark:text-gray-400 mb-1">{t('common.merke')}</div>
            <select
              value={merkeInput}
              onChange={e => { setMerkeInput(e.target.value); setModellInput('') }}
              className="w-full px-2 py-1.5 rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-sm"
            >
              <option value="">{t('priser.alleMerker')}</option>
              {merkeOptions.map(o => (
                <option key={o.merke!} value={o.merke!}>{o.merke}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <div className="text-gray-600 dark:text-gray-400 mb-1">
              {t('common.modell')}{modellLoading ? t('priser.lasterSuffix') : ''}
            </div>
            <select
              value={modellInput}
              onChange={e => setModellInput(e.target.value)}
              disabled={!merkeInput || modellLoading}
              className="w-full px-2 py-1.5 rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{merkeInput ? t('priser.alleModeller') : t('priser.velgMerkeForst')}</option>
              {modellOptions.map(o => (
                <option key={o.modell!} value={o.modell!}>{o.modell}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <div className="text-gray-600 dark:text-gray-400 mb-1">{t('priser.aarsmodellFra')}</div>
            <input
              type="number"
              value={aarFrom}
              onChange={e => setAarFrom(e.target.value)}
              placeholder="2015"
              className="w-full px-2 py-1.5 rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-sm"
            />
          </label>
          <label className="text-sm">
            <div className="text-gray-600 dark:text-gray-400 mb-1">{t('priser.aarsmodellTil')}</div>
            <input
              type="number"
              value={aarTo}
              onChange={e => setAarTo(e.target.value)}
              placeholder="2024"
              className="w-full px-2 py-1.5 rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-sm"
            />
          </label>
        </div>
      </FilterSlot>

      {error !== null && (
        <div className="card p-4 text-sm text-red-600 dark:text-red-400">
          {t('oversikt.feilVedLasting', { error: error || t('oversikt.ukjentFeil') })}
        </div>
      )}

      {loading && !data && (
        <div className="card p-4 animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      )}

      {data && (
        <>
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 px-1">
            <span>
              <Trans
                t={t}
                i18nKey="priser.viserAv"
                values={{ n: data.rows.length, total: data.total.toLocaleString('nb-NO') }}
                components={{ b: <strong /> }}
              />
              {loading && t('priser.lasterSuffix')}
            </span>
            <span>
              {t('priser.sideAv', { page: data.page, pages: totalPages })}
            </span>
          </div>

          <ObservasjonerGrid rows={data.rows} total={data.total} sort={sort} sortDir={sortDir} onSort={handleSort} />

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-sm border border-gray-200 dark:border-dark-border text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-border"
              >
                {t('pagination.forrige')}
              </button>
              <span className="text-sm tabular-nums">{t('priser.sideSlash', { page, pages: totalPages })}</span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 rounded-sm border border-gray-200 dark:border-dark-border text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-border"
              >
                {t('pagination.neste')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
